import { useEffect, useState } from "react";
import { Permissions, RegisterButtonLocation } from "@repo/database";
import { convertSecondsToHoursAndMinutes } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { differenceInSeconds } from "date-fns";
import { ClockIcon, ReceiptIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import type { Table } from "~/providers/table-provider";
import { FunctionButtons } from "~/components/button/function-buttons";
import { FloorPlanItem } from "~/components/floor-plan/floor-plan-item";
import FloorPlanGridView from "~/components/floor-plan/grid-view";
import { FloorPlanMapView } from "~/components/floor-plan/map-view";
import { SectionButtons } from "~/components/floor-plan/section-buttons";
import TableContextDialog from "~/components/floor-plan/table-context-dialog";
import { Button } from "~/components/ui/button";
import { useConfiguration } from "~/hooks/use-configuration";
import { useCustomLongPress } from "~/hooks/use-long-press";
import { useOrder } from "~/hooks/use-order";
import { useTable } from "~/hooks/use-table";
import { cn } from "~/lib/utils";
import { useOrderManager } from "~/managers/use-order-manager";
import { usePermissionManager } from "~/managers/use-permission-manager";
import { useTableManager } from "~/managers/use-table-manager";
import { useAlertStore } from "~/stores/use-alert-store";
import { useOrderStore } from "~/stores/use-order-store";
import { usePreferencesStore } from "~/stores/use-preferences-store";
import { useRegisterStore } from "~/stores/use-register-store";

export const Route = createFileRoute("/_main/floor-plan/")({
  component: FloorPlan,
});

const ItemWithOrder = ({ table }: { table: Table }) => {
  const { orders } = useOrder();

  const { showDialog } = useAlertStore(
    useShallow((state) => ({
      showDialog: state.showDialog,
    })),
  );

  const { t } = useTranslation("common");

  const itemsToTransfer = useOrderStore((state) => state.transferItems);

  const { hasPermission } = usePermissionManager();

  const orderOnTable = orders.find((o) => o.tableId === table.id);

  const { cashRegisterConfiguration } = useConfiguration();

  const { openTableContext } = useTableManager();

  const navigate = useNavigate();

  const { floorPlanMode } = usePreferencesStore();

  const { transferItems, cancelTransfer } = useOrderManager();

  const handleTableContextMenu = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    openTableContext(table);
  };

  const attrs = useCustomLongPress(
    () => {
      handleTableContextMenu();
    },
    {
      threshold: 500,
      movementThreshold: 10,
    },
  );

  const handleTableClick = async () => {
    if (itemsToTransfer.length > 0) {
      const result = await showDialog({
        title: t("Alert.general.please_confirm"),
        message: t("Transfer.transfer_items_confirm", {
          table_to: table.reference,
        }),
        buttons: [
          {
            label: t("cancel"),
            value: "cancel",
          },
          {
            label: t("ok"),
            value: "ok",
          },
        ],
      });

      if (result === "ok") {
        await transferItems({
          items: itemsToTransfer,
          tableId: table.id,
          orderId: orderOnTable?.id,
        });
      } else {
        cancelTransfer();
      }
    } else {
      //TODO: move this to route level
      const { permission } = await hasPermission({
        requiredPermission: Permissions.view_order,
        label: t("Permissions.view_order_label"),
      });

      if (!permission) return;

      if (orderOnTable) {
        void navigate({
          to: "/cash-register",
          search: {
            orderId: orderOnTable.id,
            tableId: table.id,
          },
        });
      } else {
        void navigate({
          to: "/cash-register",
          search: {
            tableId: table.id,
          },
        });
      }
    }
  };

  const [secondsSinceLastInteraction, setSecondsSinceLastInteraction] =
    useState(0);

  useEffect(() => {
    if (!orderOnTable?.updatedAt) return;

    const updateSecondsSinceLastInteraction = () => {
      const diff = differenceInSeconds(Date.now(), orderOnTable.updatedAt);
      // Only update state if the minute value has changed
      setSecondsSinceLastInteraction((prevSeconds) => {
        const prevMinutes = Math.floor(prevSeconds / 60);
        const newMinutes = Math.floor(diff / 60);
        return prevMinutes !== newMinutes ? diff : prevSeconds;
      });
    };

    // Set initial value
    updateSecondsSinceLastInteraction();

    // Still check every second, but only update state when minute changes
    const interval = setInterval(() => {
      updateSecondsSinceLastInteraction();
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [orderOnTable]);

  if (floorPlanMode === 1) {
    return (
      <div
        style={{
          position: "absolute",
          left: table.posX,
          top: table.posY,
        }}
      >
        <FloorPlanItem
          table={table}
          onContextMenu={() => openTableContext(table)}
          onClick={handleTableClick}
          className={cn(
            orderOnTable &&
              secondsSinceLastInteraction <
                cashRegisterConfiguration.orderAttentionCheckThreshold &&
              "fill-green-400",
            orderOnTable &&
              secondsSinceLastInteraction >=
                cashRegisterConfiguration.orderAttentionCheckThreshold &&
              secondsSinceLastInteraction <
                cashRegisterConfiguration.orderAttentionLateThreshold &&
              "fill-yellow-400",
            orderOnTable &&
              secondsSinceLastInteraction >=
                cashRegisterConfiguration.orderAttentionLateThreshold &&
              "fill-red-400",
          )}
        >
          {orderOnTable?.ticketPrinted && (
            <div className="absolute right-4 bottom-4">
              <ReceiptIcon className="stroke-black" size={15} />
            </div>
          )}
        </FloorPlanItem>
      </div>
    );
  }

  return (
    <button
      {...attrs}
      onContextMenu={(e) => {
        e.preventDefault();
        openTableContext(table);
      }}
      onClick={handleTableClick}
      className={cn(
        "w-full h-[100px] text-xl flex flex-col justify-between select-none p-2 bg-background-tertiary shadow",
        orderOnTable &&
          secondsSinceLastInteraction <
            cashRegisterConfiguration.orderAttentionCheckThreshold &&
          "bg-green-500",
        orderOnTable &&
          secondsSinceLastInteraction >=
            cashRegisterConfiguration.orderAttentionCheckThreshold &&
          secondsSinceLastInteraction <
            cashRegisterConfiguration.orderAttentionLateThreshold &&
          "bg-yellow-500",
        orderOnTable &&
          secondsSinceLastInteraction >=
            cashRegisterConfiguration.orderAttentionLateThreshold &&
          "bg-red-500",
      )}
    >
      <div>{t("Cart.table_reference", { reference: table.reference })}</div>
      {orderOnTable && (
        <div className="flex flex-row justify-end gap-2 w-full">
          <div className="flex flex-row gap-1 items-center">
            <span>
              {convertSecondsToHoursAndMinutes(secondsSinceLastInteraction)}
            </span>
            <ClockIcon size={20} />
          </div>
        </div>
      )}
    </button>
  );
};

function FloorPlan() {
  const { tables } = useTable();

  const { selectedSection } = useRegisterStore();

  const transferItems = useOrderStore((state) => state.transferItems);

  const { t } = useTranslation("common");

  const { cancelTransfer } = useOrderManager();

  const { floorPlanMode } = usePreferencesStore();

  return (
    <div className="flex flex-col h-full overflow-auto w-full">
      {transferItems.length > 0 && (
        <div className="flex items-center justify-center h-12 animate-pulse bg-red-500">
          <div className="flex gap-2 items-center">
            <p className="text-white font-bold">
              {t("Order.transfer_items_select_table")}
            </p>
            <Button
              onClick={() => {
                cancelTransfer();
              }}
            >
              {t("cancel")}
            </Button>
          </div>
        </div>
      )}
      <div className="shadow p-1">
        <SectionButtons />
      </div>
      {floorPlanMode === 1 && (
        <div className="overflow-hidden flex-1 flex items-center justify-center">
          <FloorPlanMapView>
            {tables.map((table) => (
              <ItemWithOrder key={table.id} table={table} />
            ))}
          </FloorPlanMapView>
          <TableContextDialog />
        </div>
      )}
      {floorPlanMode === 2 && (
        <div className="overflow-auto small-scrollbar flex-1">
          <FloorPlanGridView>
            {tables
              .filter((table) => table.locationId === selectedSection?.id)
              .sort((a, b) => a.position - b.position)
              .map((table) => (
                <ItemWithOrder key={table.id} table={table} />
              ))}
          </FloorPlanGridView>
          <TableContextDialog />
        </div>
      )}

      <div className="border-t">
        <FunctionButtons location={RegisterButtonLocation.floor_plan_footer} />
      </div>
    </div>
  );
}
